import React, { useEffect, useState } from 'react';
import ServiceSelector from './ServiceSelector';
import ImageUpload from './ImageUpload';
import ResultDisplay from './ResultDisplay';
import CTASection from './CTASection';
import { useImageProcessing } from '../hooks/useImageProcessing';

const AIApp = () => {
  const [selectedService, setSelectedService] = useState('hifu');
  const [selectedDetail, setSelectedDetail] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [strength, setStrength] = useState(0.6);

  const { loading, result, error, processImage, downloadResult, reset } = useImageProcessing();

  const services = {
    botox: {
      icon: '⚡',
      label: 'Botox Giãn Cơ',
      serviceType: 'image2image',
      headline: 'Hết Nhăn Nhưng Vẫn Tự Nhiên – Botox Chuẩn Đẹp Không Lộ Dấu',
      description: 'Botox giúp thư giãn cơ tạo nhăn, mang lại làn da mượt mà và ánh nhìn tươi tắn. Không làm thay đổi cấu trúc da, không làm mặt đơ. Công nghệ tiêm tinh chỉnh theo cơ từng người, đẹp tự nhiên như chưa từng can thiệp.',
      note: 'Đây là khuôn mặt mới được AI tạo ra – Trải nghiệm Botox thực tế tại Vanilla Clinic!',
      details: ['Trán', 'Chân chim', 'Cau mày (glabella)', 'Nhăn mũi (bunny lines)']
    },

    filler: {
      icon: '🌸',
      label: 'Filter làm đẹp',
      serviceType: 'image2image',
      headline: 'Filler Hyaluronic Acid – Định Hình Khuôn Mặt Mà Vẫn Giữ Nét Gốc',
      description: 'Tạo nét mềm mại, đầy đặn với filler HA chuẩn y khoa. Kỹ thuật tiêm chính xác đảm bảo không lệch, không vón, giúp cằm, mũi, môi đẹp tự nhiên như chưa can thiệp.',
      note: 'Vẻ đẹp lý tưởng được AI tạo ra – đạt được với liệu trình tại Vanilla Clinic!',
      details: ['Mũi (sống – chóp)', 'Cằm', 'Gò má', 'Má baby', 'Rãnh cười (nasolabial)', 'Rãnh mũi – khóe miệng', 'Môi', 'Làm đầy sẹo lõm']
    },

    hifu: {
      icon: '⚡',
      label: 'Hifu Nâng Cơ',
      serviceType: 'image2image',
      headline: 'HIFU Nâng Cơ Từ Trong Ra Ngoài – Không Xâm Lấn, Không Nguy Hiểm',
      description: 'Sóng HIFU hội tụ tạo nhiệt điểm tại lớp SMAS, giúp co cơ, kích collagen. Không xâm lấn, không tổn thương da, hiệu quả tự nhiên',
      note: 'Đây là khuôn mặt mới được AI tạo ra – Trải nghiệm HIFU thực tế tại Vanilla Clinic!',
      details: ['Má giữa', 'Hàm dưới & Nọng cằm (Jowl)', 'Trán & chân mày', 'Cổ']
    },

    laser: {
      icon: '💎',
      label: 'Laser Trị Nám',
      serviceType: 'image2image',
      headline: 'Laser Toning – Da Sáng Hơn, Đều Màu, Không Đỏ, Không Lột',
      description: 'Da sáng tự nhiên, đều màu, mờ thâm với Laser Toning. Không lột, không đỏ, an toàn, nhẹ nhàng, hiệu quả nhanh, phù hợp mọi vùng da mặt và cơ thể.',
      note: 'Làn da mới được AI tạo ra – Laser Pico tại Vanilla Clinic mang lại kết quả tương tự!',
      details: ['Toàn mặt']
    }
  };

  const handleServiceChange = (service) => {
    setSelectedService(service);
    setSelectedDetail(""); // reset detail
    reset(); // reset result
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);

    const url = URL.createObjectURL(file);
    setOriginalImageUrl(url);
    console.log('📷 Original image URL set:', url);

    reset(); // reset old result
  };

  const handleProcess = async () => {
    if (selectedFile) {
      console.log('🚀 Processing with originalImageUrl:', originalImageUrl);

      await processImage(
        selectedService,      // dịch vụ
        selectedFile,         // ảnh
        strength,             // mức độ
        currentService.serviceType, // loại task
        selectedDetail        // CHI TIẾT DỊCH VỤ
      );
    }
  };

  useEffect(() => {
    return () => {
      if (originalImageUrl) {
        URL.revokeObjectURL(originalImageUrl);
      }
    };
  }, [originalImageUrl]);

  const currentService = services[selectedService];

  return (
    <main id="ai-app-section" className="max-w-6xl mx-auto p-6 md:p-12 scroll-mt-20">
      <div className="bg-white p-6 md:p-10 rounded-2xl shadow-xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-10">

          {/* Cột trái */}
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
              {currentService.headline}
            </h2>

            <p className="text-lg text-gray-600 mb-6">
              {currentService.description}
            </p>

            {/* Chọn loại dịch vụ */}
            <ServiceSelector
              selectedService={selectedService}
              onServiceChange={handleServiceChange}
              services={services}
            />

            {/* Chọn chi tiết dịch vụ */}
            {currentService.details && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Chọn chi tiết vùng
                </label>
                <select
                  value={selectedDetail}
                  onChange={(e) => setSelectedDetail(e.target.value)}
                  className="mt-1 block w-full border rounded-lg p-3"
                >
                  <option value="">-- Chọn chi tiết --</option>
                  {currentService.details.map((detail, idx) => (
                    <option key={idx} value={detail}>
                      {detail}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Slider strength */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Intensity (mức thay đổi)</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={strength}
                onChange={(e) => setStrength(parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="text-sm text-gray-500">Current: {strength}</div>
            </div>

            {/* Upload ảnh */}
            <ImageUpload
              onFileSelect={handleFileSelect}
              selectedFile={selectedFile}
              onProcess={handleProcess}
              isLoading={loading}
            />



          </div>

          {/* Cột phải */}
          <div className="mt-8 lg:mt-0">
            <ResultDisplay
              originalImage={originalImageUrl}
              resultImage={result?.dataUrl}
              resultMeta={result?.response}
              isLoading={loading}
              error={error}
              onDownload={downloadResult}
              serviceName={currentService.label}
            />
          </div>

        </div>
      </div>


      {/* CTA khi có kết quả */}
      {result && (
        <CTASection
          selectedService={selectedService}
          services={services}
        />
      )}

    </main>
  );
};

export default AIApp;
